import React from 'react'
import { FormattedMessage } from 'react-intl'
import { css } from '@emotion/css'
import { HttpRequest, UserInput, AttackEventDetails } from '../shared'
import { AttackEventRequest } from '../attack-event-request'
import { AttackStatus } from '../../attack-status'
import {
  LegacySuspiciousEventDescription,
  AttackEventResultCodeSnippet
} from '../attack-event-overview-view'

export enum PathTraversalFindings {
  CUSTOM_CODE_ACCESSING_SYSTEM_FILES = 'CUSTOM_CODE_ACCESSING_SYSTEM_FILES',
  COMMON_FILE_EXPLOITS = 'COMMON_FILE_EXPLOITS'
}

const PathTraversalFindingsi18lnConfig: Record<PathTraversalFindings, string> =
  {
    [PathTraversalFindings.CUSTOM_CODE_ACCESSING_SYSTEM_FILES]:
      'detail_attackEventFindings_customCodeAccessingSystemFiles',
    [PathTraversalFindings.COMMON_FILE_EXPLOITS]:
      'detail_attackEventFindings_commonFileExploitsAttack'
  }

const containerCss = css`
  padding-bottom: 20px;
`

export function PathTraversalSemanticAnalysisOverview({
  httpRequest,
  userInput,
  details,
  sourceIP,
  callLocation,
  result
}: {
  readonly httpRequest?: HttpRequest
  readonly userInput?: UserInput
  readonly sourceIP?: string
  readonly details: AttackEventDetails
  readonly callLocation?: string
  readonly result: AttackStatus
}) {
  const path = !!details.path ? details.path : ''
  const findings = !!details.findings
    ? details.findings.map((finding) => PathTraversalFindings[finding])
    : []
  return (
    <React.Fragment>
      <PathTraversalSemanticAnalysisRuleSourceOverview
        request={httpRequest}
        sourceIP={sourceIP}
        userInput={userInput}
      />
      <PathTraversalSemanticAnalysisRuleResultOverview
        callLocation={callLocation}
        details={{ path }}
      />
      <PathTraversalSemanticAnalysisRuleNoSourceOverview sourceIP={sourceIP} />
      <LegacySuspiciousEventDescription
        containerCss={containerCss}
        result={result}
      />
      <PathTraversalSemanticAnalysisRuleDetailsOverview
        details={{ findings }}
      />
    </React.Fragment>
  )
}

export function PathTraversalSemanticAnalysisRuleSourceOverview({
  request,
  userInput,
  sourceIP
}: {
  readonly request?: HttpRequest
  readonly userInput?: UserInput
  readonly sourceIP?: string
}) {
  return (
    <React.Fragment>
      {sourceIP && userInput && request && (
        <div className={containerCss}>
          <FormattedMessage id="detail_attackEventSource_observingHttpRequest" />
          <AttackEventRequest
            request={request}
            userInput={userInput}
            verbose={false}
            showTaintedValues={false}
          />
        </div>
      )}
    </React.Fragment>
  )
}

export function PathTraversalSemanticAnalysisRuleNoSourceOverview({
  sourceIP
}: {
  readonly sourceIP?: string
}) {
  return (
    <React.Fragment>
      {!sourceIP && (
        <div className={containerCss}>
          <FormattedMessage id="detail_attackEventSource_sourceUnknownNoHttpRequest" />
        </div>
      )}
    </React.Fragment>
  )
}

export function PathTraversalSemanticAnalysisRuleResultOverview({
  details: { path },
  callLocation
}: {
  readonly details: {
    readonly path?: string
  }
  readonly callLocation?: string
}) {
  return (
    <React.Fragment>
      {path && (
        <div className={containerCss}>
          <FormattedMessage
            id="detail_attackEventResult_suspiciousElementsInFileOpeningCall"
            values={{
              callLocation
            }}
          />
          <AttackEventResultCodeSnippet result={path} />
        </div>
      )}
    </React.Fragment>
  )
}

export function PathTraversalSemanticAnalysisRuleDetailsOverview({
  details: { findings }
}: {
  readonly details: {
    readonly findings: PathTraversalFindings[]
  }
}) {
  return (
    <React.Fragment>
      {Array.isArray(findings) && findings.length > 0 && (
        <div className={containerCss}>
          <FormattedMessage
            id="detail_attackEventDetails_filePathContainsSuspiciousElementPlural"
            values={{ count: findings.length }}
          />
          <ul>{findings.map((finding) => renderFindingsList(finding))}</ul>
        </div>
      )}
    </React.Fragment>
  )
}

const renderFindingsList = (finding: PathTraversalFindings) => {
  const findingID =
    finding && PathTraversalFindingsi18lnConfig[finding]
      ? PathTraversalFindingsi18lnConfig[finding]
      : undefined

  return (
    findingID && (
      <React.Fragment key={findingID}>
        {findingID && (
          <li>
            <FormattedMessage id={findingID} />
          </li>
        )}
      </React.Fragment>
    )
  )
}
